import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { 
  CloudArrowUpIcon, 
  DocumentIcon, 
  PhotoIcon, 
  VideoCameraIcon,
  SpeakerWaveIcon,
  XMarkIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://api.mythicalarc.com/api'
  : 'http://localhost:8000/api';

const MediaUpload = ({ onUploadComplete }) => {
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploading, setUploading] = useState(false);

  const getFileIcon = (file) => {
    const type = file.type.split('/')[0];
    switch (type) {
      case 'image': return <PhotoIcon className="w-8 h-8 text-blue-500" />;
      case 'video': return <VideoCameraIcon className="w-8 h-8 text-purple-500" />;
      case 'audio': return <SpeakerWaveIcon className="w-8 h-8 text-green-500" />;
      default: return <DocumentIcon className="w-8 h-8 text-gray-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      id: Math.random().toString(36).substring(7),
      status: 'pending',
      progress: 0,
      result: null,
      error: null
    }));
    
    setUploadQueue(prev => [...prev, ...newFiles]);
  }, []);

  const removeFile = (fileId) => {
    setUploadQueue(prev => prev.filter(item => item.id !== fileId));
  };

  const uploadFiles = async () => {
    setUploading(true);
    
    const pendingFiles = uploadQueue.filter(item => item.status === 'pending');
    
    for (const fileItem of pendingFiles) {
      try {
        // Update status to uploading
        setUploadQueue(prev => prev.map(item => 
          item.id === fileItem.id 
            ? { ...item, status: 'uploading', progress: 0 }
            : item
        ));

        const formData = new FormData();
        formData.append('file', fileItem.file);
        formData.append('generate_caption', 'true');
        formData.append('extract_text', 'true');

        const response = await axios.post(`${API_BASE_URL}/media/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadQueue(prev => prev.map(item =>
              item.id === fileItem.id
                ? { ...item, progress: percentCompleted }
                : item
            ));
          }
        });

        // Update with success
        setUploadQueue(prev => prev.map(item =>
          item.id === fileItem.id
            ? { ...item, status: 'completed', progress: 100, result: response.data }
            : item
        ));

        // Notify parent component
        if (onUploadComplete) {
          onUploadComplete(response.data);
        }

      } catch (error) {
        // Update with error
        setUploadQueue(prev => prev.map(item =>
          item.id === fileItem.id
            ? { 
                ...item, 
                status: 'error', 
                error: error.response?.data?.detail || error.message 
              }
            : item
        ));
      }
    }
    
    setUploading(false);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.bmp', '.tiff', '.webp'],
      'video/*': ['.mp4', '.avi', '.mov', '.mkv', '.wmv', '.flv', '.webm'],
      'audio/*': ['.mp3', '.wav', '.m4a', '.flac', '.ogg', '.aac'],
      'application/pdf': ['.pdf'],
      'text/*': ['.txt']
    },
    maxSize: 100 * 1024 * 1024, // 100MB
    multiple: true
  });

  return (
    <div className="space-y-6">
      {/* Drop Zone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors duration-200 ${
          isDragActive
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
        }`}
      >
        <input {...getInputProps()} />
        <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        {isDragActive ? (
          <p className="text-lg text-blue-600">Drop the files here...</p>
        ) : (
          <div>
            <p className="text-lg text-gray-600 mb-2">
              Drag & drop files here, or click to select
            </p>
            <p className="text-sm text-gray-500">
              Supports images, videos, audio files, and documents (Max 100MB each)
            </p>
          </div>
        )}
      </div>

      {/* Upload Queue */}
      {uploadQueue.length > 0 && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">
              Upload Queue ({uploadQueue.length} files)
            </h3>
            {uploadQueue.some(item => item.status === 'pending') && (
              <button
                onClick={uploadFiles}
                disabled={uploading}
                className="btn-primary disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload All'}
              </button>
            )}
          </div>

          <div className="space-y-3">
            {uploadQueue.map((item) => (
              <div
                key={item.id}
                className="bg-white border border-gray-200 rounded-lg p-4 flex items-center space-x-4"
              >
                {/* File Icon */}
                <div className="flex-shrink-0">
                  {getFileIcon(item.file)}
                </div>

                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {item.file.name}
                    </h4>
                    <div className="flex items-center space-x-2">
                      {item.status === 'completed' && (
                        <CheckCircleIcon className="w-5 h-5 text-green-500" />
                      )}
                      {item.status === 'pending' && (
                        <button
                          onClick={() => removeFile(item.id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-500">
                    {formatFileSize(item.file.size)} • {item.file.type}
                  </p>

                  {/* Progress Bar */}
                  {item.status === 'uploading' && (
                    <div className="mt-2">
                      <div className="bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${item.progress}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{item.progress}%</p>
                    </div>
                  )}

                  {/* Success Info */}
                  {item.status === 'completed' && item.result && (
                    <div className="mt-2 text-xs text-green-600">
                      <p>✅ Processed successfully</p>
                      {item.result.caption && (
                        <p className="text-gray-600">Caption: {item.result.caption}</p>
                      )}
                      {item.result.extracted_text && item.result.extracted_text.length > 0 && (
                        <p className="text-gray-600">
                          Text extracted: {item.result.extracted_text.substring(0, 100)}...
                        </p>
                      )}
                      {item.result.transcription && (
                        <p className="text-gray-600">
                          Transcription: {item.result.transcription.substring(0, 100)}...
                        </p>
                      )}
                    </div>
                  )}

                  {/* Error Info */}
                  {item.status === 'error' && (
                    <p className="text-xs text-red-600 mt-2">
                      ❌ Error: {item.error}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaUpload;
